import { IScope } from "angular";
import { ImportDocument } from "../documents";
import { IOmniaShell } from "../shell";
import { IOmniaSettings } from "../types";
import { selectMany } from "../utils/array";
import { isArray, isObject, isUndefined, isUndefinedOrNull } from "../utils/utils";
import { createCustomBasemaps } from "./basemaps/custom";
import { MapEditInteraction } from "./editInteraction";
import { Layer } from "./layer";
import { Map } from "./map";
import { MeasuringTool } from "./measuring/measuring-tool";
import { IMapMeasuringService, IMapMeasuringTool } from "./measuring/types";
import { createPictureOverlay } from "./pictureOverlay";
import { MapSelectInteraction } from "./selectInteraction";
import {
    IBasemapGroup,
    ILayer,
    IMapAutoEditConfig,
    IMapContainer,
    IMapContainerContext,
    IMapContainerOptions,
    IMapContainerUserOptions,
    IMapDetailsBox,
    IMapKey,
    IMapLabelOptions,
    IMapSelectInteraction,
    IMapTooltip,
    MapViewState,
    OpenLayersStyleProvider,
} from "./types";
import { ol_Extent, ol_Feature, ol_style, ol_geom, ol_GeometryType, ol_Coordinate, ol_FeatureLike, ol_Overlay } from "@omnia/exports/openlayers";


export abstract class MapContainer<TContext extends IMapContainerContext, TOptions extends IMapContainerOptions>
    implements IMapContainer
{
    protected _disposed = false;

    public abstract getDefaultExtent(): ol_Extent;
    public abstract showFeaturesWithData(items: any[], select: boolean): void;

    protected abstract updateSelectionFromMap(features: any[]): void;
    protected abstract setupLayers(): void;
    protected abstract updateMapSelection(): void;
    protected abstract setupLayerWatcher(): void;

    protected _labels: IMapLabelOptions = { declutter: true, deduplicate: true };

    protected options: TOptions;
    protected ctxt: TContext;
    protected settings: IOmniaSettings;

    private additionalBasemaps: IBasemapGroup[] = [];

    protected layers: Layer[];

    protected _map: Map;

    protected _select: IMapSelectInteraction;
    private selectionSync: boolean;

    protected _measure: MeasuringTool;

    protected _edit: MapEditInteraction;
    private _editContextMenuState: boolean;

    public synchronizeSelectionFromMap = true;

    public detailsBox: IMapDetailsBox;
    public keys: IMapKey[];

    protected _allowTracking: boolean;
    protected _canViewTrackingInfo: boolean;

    protected setup(ctxt: TContext, options: TOptions, settings: IOmniaSettings) {
        this.ctxt = ctxt;
        this.options = options;
        this.settings = settings;

        this._map = new Map(ctxt, settings, () => this.getDefaultExtent());

        this.additionalBasemaps = this.setupBasemaps();

        this.setupTracking(ctxt.shell);

        this.setupMapSelection(options?.selectionFilter);

        this.setupMeasuringTool();

        this.setupMapEditing();

        this.setupLayers();
    }

    public dispose() {
        if (this._measure) {
            this._measure.dispose();
            this._measure = null;
        }

        if (this._edit) {
            this._edit.dispose();
            this._edit = null;
        }

        if (this._select) {
            this._select.dispose();
            this._select = null;
        }

        if (this._map) {
            this._map.dispose();
            this._map = null;
        }

        this._disposed = true;
    }

    protected setupBasemaps() {
        const { _map: map, ctxt, settings } = this;

        const targetLayers = map.customBasemapsGroup.getLayers();

        const customBasemaps = createCustomBasemaps(ctxt.customBasemaps, targetLayers, settings);

        return customBasemaps ? [customBasemaps] : [];
    }

    isBasemapAvailable(group: IBasemapGroup) {
        return true;
    }

    private setupTracking(shell: IOmniaShell) {
        const session = shell.getSession();
        const debug = session.debugMode;

        this._allowTracking = shell.mobile || debug;
        this._canViewTrackingInfo = debug || session.getUserProperty("GeolocationInfo");
    }

    private setupMapSelection(filter: (feature: ol_Feature) => boolean) {
        this._select = this.createSelectInteraction(filter);

        this._select.setStyleProvider((feature, resolution) => Layer.getSelectedStyle(feature, resolution));

        this._select.watchSelection((features: any[]) => {
            this.updateSelectionFromMap(features);

            this.ctxt.$rootScope.$applyAsync();
        });
    }

    protected createSelectInteraction(filter?: (feature: ol_Feature) => boolean): IMapSelectInteraction {
        const factory = this.options.selecInteractionFactory;

        if (factory) {
            return factory(this._map);
        }

        return new MapSelectInteraction(this._map.getMap(), filter);
    }

    private setupMeasuringTool() {
        this._measure = new MeasuringTool(this, this.ctxt.$timeout);
    }

    getMeasuringService(): IMapMeasuringService {
        return this.ctxt.measuringService;
    }

    getMeasuringTool(): IMapMeasuringTool {
        return this._measure;
    }

    private setupMapEditing() {
        this._edit = new MapEditInteraction(
            this._map.getMap(),
            this._select,
            this.ctxt.$timeout,
            this.ctxt.measuringService
        );
    }

    get map() {
        return this._map;
    }

    getMap() {
        return this._map.getMap();
    }

    getView() {
        return this._map.getView();
    }

    get enableTooltips() {
        return this._map.enableTooltips;
    }

    set enableTooltips(value) {
        this._map.enableTooltips = value;
    }

    getLayers(): Layer[] {
        return this.layers ? this.layers.slice() : null;
    }

    resetDefaultLayers(resetVisibility: boolean = false) {
        if (!this._map) return;

        this.setupLayers();

        this.layers.forEach((l) => {
            l.clearHighlight();
            if (resetVisibility) {
                l.isVisible = true;
            }
        });
    }

    getViewState() {
        return this._map?.getViewState();
    }

    setViewState(state: MapViewState) {
        this._map.setViewState(state);
    }

    getCurrentViewExtent(): ol_Extent {
        return this._map ? this._map.getCurrentViewExtent() : null;
    }

    viewDefaultExtent() {
        this._map.viewDefaultExtent();
    }

    viewExtent(extent: ol_Extent | ol_Feature | ol_geom.Geometry | { items: (ol_Feature | ol_geom.Geometry)[] }) {
        function checkArray(a: any): a is { items: (ol_Feature | ol_geom.Geometry)[] } {
            return isObject(a) && isArray(a["items"]);
        }

        if (checkArray(extent)) {
            const geometries = extent.items.map((f) => (f instanceof ol_Feature ? f.getGeometry() : f));
            const collection = new ol_geom.GeometryCollection(geometries);
            extent = collection.getExtent();
        }

        if (extent instanceof ol_Feature) {
            extent = extent.getGeometry();
        }

        return this._map.viewExtent(extent);
    }

    setCenter(center: ol_Extent | ol_Feature | ol_geom.Geometry | ol_Coordinate) {
        this._map.setCenter(center);
    }

    getExtentForDataItem(data: any) {
        const feature = this.findFeatureWithData(data);
        return feature ? feature.getGeometry().getExtent() : null;
    }

    viewExtentForDataItem(data: any) {
        const feature = this.findFeatureWithData(data);

        if (feature) {
            return this._map.viewExtent(feature.getGeometry());
        }
    }

    viewExtentForDataItems(data: any[]) {
        let geometries = this.findFeaturesWithData(data).map((f) => f.getGeometry());

        if (geometries.length) {
            const collection = new ol_geom.GeometryCollection(geometries);
            return this.viewExtent(collection.getExtent());
        }
    }

    addLayer(layer: Layer, selectable = true, topmost = false) {
        if (this.layers.find((l) => l == layer)) {
            if (!topmost) return;
            this.removeLayer(layer);
        }

        this.layers.push(layer);
        this._map.addLayer(layer);

        if (selectable) {
            this._select.addLayer(layer);
        }
    }

    removeLayer(layer: Layer) {
        if (this._map) {
            this._map.removeLayer(layer);
            this._select.removeLayer(layer);
            this.layers.remove((l) => l == layer);
        }
    }

    setTopmostLayer(layer: Layer) {
        this.addLayer(layer, layer.selectable, true);
    }

    setSelectedStyleProvider(
        provider: (feature: ol_FeatureLike, resolution: number) => ol_style.Style[]
    ) {
        this._select.setStyleProvider(provider);
    }

    setSelectableLayers(layers: Layer[]) {
        this._select.setLayers(layers, true);
    }

    autoEdit(config: IMapAutoEditConfig) {
        this._edit.autoEdit(config);
    }

    findFeatures(predicate: (feature: any) => any): any[] {
        return Array.prototype.concat.apply(
            [],
            this.layers.map((l) => l.findFeatures(predicate))
        );
    }

    findFeaturesWithData(data: any[]): ol_Feature[] {
        return Array.prototype.concat.apply(
            [],
            this.layers.map((l) => l.findFeaturesWithData(data))
        );
    }

    findFeatureWithData(data: any): ol_Feature {
        var r = this.findFeaturesWithData([data]);

        if (r && r.length) {
            return r[0];
        }
    }

    removeFeatures(features: any[]) {
        this.layers.forEach((l) => {
            var layerSource = l.mapLayer.getSource();
            var layerFeatures = layerSource.getFeatures();

            features.forEach((f) => {
                if (layerFeatures.find((lf) => lf == f)) {
                    layerSource.removeFeature(f);
                }
            });
        });
    }

    removeFeaturesWithData(item: any) {
        var features = this.findFeaturesWithData([item]);

        this.removeFeatures(features);
    }

    removeFeaturesWithNoData() {
        var features = [];

        this.layers.forEach((l) => {
            var layerSource = l.mapLayer.getSource();
            var layerFeatures = layerSource.getFeatures();

            features = features.concat(layerFeatures.filter((f) => isUndefined(f.get("data"))));
        });

        this.removeFeatures(features);
    }

    selectFeatures(features: ol_Feature[]) {
        this._select.selectFeatures(features);
    }

    getSelectedFeatures(): ol_Feature[] {
        return this._select.getSelectedFeatures();
    }

    getSelectedFeaturesCount(): number {
        return this._select.getSelectedFeaturesCount();
    }

    protected synchronizeSelection(action: () => void): boolean {
        if (this.selectionSync) return false;

        try {
            this.selectionSync = true;

            action();
        } finally {
            this.selectionSync = false;

            return true;
        }
    }

    getEditMode(): boolean {
        return this._edit.getEditMode();
    }

    getDrawingMode(): boolean {
        return this._edit.getDrawingMode();
    }

    startDrawingNewFeature(
        targetLayer: Layer,
        type: ol_GeometryType | "Box" | "Square" = "Polygon",
        options?: {
            drawStartWatcher?: (feature: ol_Feature) => void;
            drawEndWatcher?: (feature: ol_Feature) => void;
            style?: ol_style.Style[] | OpenLayersStyleProvider;
            keepSelection?: boolean;
        }
    ) {
        if (this._edit.startDrawing(targetLayer.mapLayer, type, options)) {
            this._editContextMenuState = !!this.enableContextMenu;
            this.enableContextMenu = false;

            this._map.setTooltip(null);
            return true;
        }
    }

    startEditFeature(feature: ol_Feature, fitExtent: boolean) {
        if (fitExtent && feature) {
            this.map.viewExtent(feature.getGeometry());
        }

        if (this._edit.startEdit(feature)) {
            this._editContextMenuState = !!this.enableContextMenu;
            this.enableContextMenu = false;
            return feature;
        }
    }

    startEditFeatureWithData(item: any, fitExtent: boolean) {
        var feature = null;

        const items: any[] = Array.isArray(item) ? item : [item];

        if (fitExtent) {
            feature = this.showFeaturesWithData(items, false);
        } else {
            var results = this.findFeaturesWithData(items);
            if (results.length == 1) feature = results[0];
        }

        if (feature) {
            if (this._edit.startEdit(feature)) {
                return feature;
            }
        }
    }

    cancelEdit() {
        this._edit.cancelAllEdits();
        this.enableContextMenu = this._editContextMenuState;
        this._editContextMenuState = undefined;
    }

    canCommit(): boolean {
        return this._edit.canCommit();
    }

    commitEdit(): ol_Feature | boolean {
        let feature: ol_Feature;
        let editResult: boolean;

        if (this._edit.getDrawingMode()) {
            feature = this._edit.endDrawing(false);
        }

        if (this._edit.getEditMode()) {
            editResult = this._edit.endEdit(false);
        }

        if (!isUndefinedOrNull(this._editContextMenuState)) {
            this.enableContextMenu = this._editContextMenuState;
            this._editContextMenuState = undefined;
        }

        return feature || editResult;
    }

    getEditedFeatureArea(): number {
        return this._edit.getEditedFeatureArea();
    }

    clearSelection() {
        this._select.clearSelection();
    }

    watchSelection(watcher: (features: ol_Feature[]) => void): Function {
        const s = this._select;

        s.watchSelection(watcher);

        return () => {
            s.unwatchSelection(watcher);
        };
    }

    unwatchSelection(watcher: (features: any[]) => void) {
        if (this._select) {
            this._select.unwatchSelection(watcher);
        }
    }

    refresh(layers = false) {
        this._map.refresh();
        this._select.refresh();
        this._edit.refresh();

        if (layers) {
            this._map.layers.forEach((l) => l.refresh());
        }
    }

    public getFeatureArea(feature: ol_geom.Geometry | ol_Feature): number {
        return this.ctxt.measuringService.getArea(feature);
    }

    get enableSelection() {
        return this._select.active;
    }

    set enableSelection(value: boolean) {
        this._select.active = value;
    }

    get enableMultipleSelection() {
        return this._select.enableMultipleSelection;
    }

    set enableMultipleSelection(value: boolean) {
        this._select.enableMultipleSelection = value;
    }

    get enableContextMenu() {
        return this._map.enableContextMenu;
    }

    set enableContextMenu(value: boolean) {
        if (!isUndefinedOrNull(value)) {
            this._map.enableContextMenu = value;
        }
    }

    get tooltip() {
        return this._map.tooltip;
    }

    set tooltip(value: IMapTooltip) {
        this._map.setTooltip(value);
    }

    watchTooltip(watcher: (tooltip: IMapTooltip) => void) {
        return this._map.watchTooltip(watcher);
    }

    addTooltipProvider(provider: (tooltip: IMapTooltip) => boolean): Function {
        return this._map.addTooltipProvider(provider);
    }

    clearTooltip() {
        this._map.clearTooltip();
    }

    public setTranslateFeatures(features: ol_Feature[]) {
        this._edit.setTranslateFeatures(features);
    }

    public setBasemapVisibility(visible: boolean, save: boolean) {
        let basemaps = this._map.basemaps;
        if (basemaps) {
            basemaps.setBasemapVisibility(visible, save);
        }
    }

    get allowTracking() {
        return this._allowTracking;
    }

    get canViewTrackingInfo() {
        return this._canViewTrackingInfo;
    }

    get tracking() {
        return this._map.tracking;
    }

    setTracking(tracking: boolean, trace: boolean) {
        this._map.setTracking(tracking, trace);
    }

    getTrackingInfo() {
        return this._map.getTrackingInfo();
    }

    protected _getUserOptions(contextLayers?: ILayer[]): IMapContainerUserOptions {
        const {
            layers,
            additionalBasemaps,
            _map: { basemaps },
        } = this;

        const baseLayers = selectMany(
            layers
                .filter((l) => l.layersPopup)
                .reverse()
                .map((l) => l.getConfigurableLayers())
        );

        return {
            basemaps,
            additionalBasemaps,
            baseLayers,
            contextLayers,
            labels: this._labels,
            canDeduplicateLabels: false,
        };
    }

    getUserOptions(): IMapContainerUserOptions {
        return this._getUserOptions();
    }

    getAdditionalBasemaps() {
        return [...this.additionalBasemaps];
    }

    getCustomOptionTemplates() {
        return null;
    }

    canSetLabelOptions() {
        return false;
    }

    canDeduplicateLabels() {
        return false;
    }

    protected updateLabelOptions(layers: Layer[], labels: IMapLabelOptions) {
        layers.forEach((l) => {
            l.updateLabelOptions(this._map, labels);
        });
    }

    onVisibilityChanged(target: ILayer | IBasemapGroup) {
        // NOP
    }

    setLabelOptions(options: IMapLabelOptions) {
        this._labels = options;
        this.updateLabelOptions(this.layers, options);
    }

    setSnappingLayers(layers: Layer[]) {
        this._edit.setSnappingLayers(layers);
    }

    sortByStackOrder<T>(layers: T[], selector: (arg: T) => Layer): T[] {
        return this.map.sortByStackOrder(layers, selector);
    }

    queryLocationApi(query: string) {
        return this.ctxt.queryLocationApi(query);
    }

    createPictureOverlays(features: { feature: ol_Feature; doc: ImportDocument }[]) {
        const { $compile, $rootScope } = this.ctxt;

        const olmap = this.map.getMap();

        const overlays = features.map((f) => {
            const { overlay, togglePopup } = createPictureOverlay($rootScope, $compile, f.feature, f.doc);

            return {
                ...f,
                overlay,
                togglePopup,
            };
        });

        for (const o of overlays) {
            olmap.addOverlay(o.overlay);
        }

        return overlays;
    }

    removeOverlays(overlays: ol_Overlay[]) {
        const olmap = this.map.getMap();

        for (const o of overlays) {
            olmap.removeOverlay(o);
        }
    }

    $apply(expr?: (scope: IScope) => void) {
        this.ctxt.$rootScope.$apply(expr);
    }
}
